import { useCallback } from 'react';
import { ApiOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Empty, Skeleton } from '../../../components/ui';
import { coQuyenModuleDaCai } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { getAccessToken } from '../../../lib/auth/oidc';
import { useAuthStore } from '../../../stores/auth';
import { useManHinhModule } from '../hooks/useModuleDaCai';
import { RanhGioiLoiModule } from './RanhGioiLoiModule';

interface Props {
  code: string;
  version: string;
  /** Điểm vào `remoteEntry.js`: đường dẫn của route tệp giao diện, hoặc URL `https` tự host. */
  entry: string;
  ten: string;
  basePath: string;
  path: string;
  navigate: (path: string) => void;
}

/**
 * Nạp và vẽ `./App` của một remote (ADR-0096): đang tải, lỗi nạp kèm "Thử lại", ranh giới lỗi riêng. Dùng chung
 * cho màn module ở cổng tenant và khối xem trước ở màn Quản lý module.
 */
export function VungRemote({ code, version, entry, ten, basePath, path, navigate }: Props) {
  const { t, ngonNgu } = useT();
  const user = useAuthStore((s) => s.user);
  const remote = useManHinhModule(code, entry);
  const coQuyen = useCallback((ma: string) => coQuyenModuleDaCai(user, ma), [user]);

  const nutThuLai = (thuLai: () => void) => (
    <Button variant="outline" size="sm" icon={<ReloadOutlined />} onClick={thuLai}>
      {t('moduleDaCai.thuLai')}
    </Button>
  );

  if (remote.isPending) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label={t('moduleDaCai.dangTai')}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (remote.isError || !remote.data) {
    return (
      <Empty
        icon={ApiOutlined}
        title={t('moduleDaCai.loiNap.tieuDe', { ten })}
        description={t('moduleDaCai.loiNap.mo')}
        action={nutThuLai(() => void remote.refetch())}
      />
    );
  }

  const ManHinh = remote.data;
  return (
    <RanhGioiLoiModule
      key={`${code}@${version}`}
      thayThe={(datLai) => (
        <Empty
          icon={WarningOutlined}
          title={t('moduleDaCai.loiChay.tieuDe', { ten })}
          description={t('moduleDaCai.loiChay.mo')}
          action={nutThuLai(datLai)}
        />
      )}
    >
      <ManHinh
        basePath={basePath}
        path={path}
        locale={ngonNgu}
        getAccessToken={getAccessToken}
        navigate={navigate}
        hasPermission={coQuyen}
      />
    </RanhGioiLoiModule>
  );
}
